import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { showNotification } from '@mantine/notifications';
import { useAuth } from '@/contexts/AuthContext';
import {
  EventAccess,
  getEventAccess,
  getOrganizerProfile,
  getPublishChecklist,
  OrganizerProfile,
  OrganizerProfileInput,
  OrganizerProfileResponse,
  PublishChecklist,
  saveOrganizerProfile,
} from '@/services/api/organizer/organizer.service';

export const EVENT_ACCESS_KEY = ['eventAccess'];

/** Eventos próprios e delegações da conta; decide menu e tela inicial. */
export const useEventAccess = () => {
  const { currentUser } = useAuth();
  return useQuery<EventAccess, AxiosError>({
    queryKey: [...EVENT_ACCESS_KEY, currentUser?.id ?? null],
    enabled: !!currentUser,
    queryFn: getEventAccess,
    staleTime: 60_000,
  });
};

export const useOrganizerProfile = () =>
  useQuery<OrganizerProfileResponse, AxiosError>({
    queryKey: ['organizerProfile'],
    queryFn: getOrganizerProfile,
  });

export const useSaveOrganizerProfile = (eventId: string) => {
  const queryClient = useQueryClient();
  return useMutation<OrganizerProfile, AxiosError, OrganizerProfileInput>({
    mutationFn: saveOrganizerProfile,
    onSuccess: () => {
      showNotification({
        color: 'green',
        title: 'Dados salvos',
        message: 'Dados do organizador salvos.',
      });
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: ['organizerProfile'] }),
        queryClient.invalidateQueries({ queryKey: ['publishChecklist', eventId] }),
      ]);
    },
  });
};

export const usePublishChecklist = (eventId: string) =>
  useQuery<PublishChecklist, AxiosError>({
    queryKey: ['publishChecklist', eventId],
    enabled: !!eventId && eventId !== 'novo',
    queryFn: () => getPublishChecklist(eventId),
  });
